import {
  BackSide,
  Color,
  DirectionalLight,
  FogExp2,
  HemisphereLight,
  MathUtils,
  Mesh,
  PMREMGenerator,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import type { WebGLRenderer } from 'three';

export interface EnvironmentOptions {
  shadows: boolean;
  shadowMapSize: number;
}

/** Linear-space palette for the late-afternoon look. */
const SKY = {
  zenith: new Color().setRGB(0.025, 0.1, 0.42),
  /** Horizon away from the sun; also the distance haze colour. */
  horizon: new Color().setRGB(0.5, 0.56, 0.72),
  /** Horizon glow toward the sun. */
  horizonSun: new Color().setRGB(1.0, 0.58, 0.32),
  ground: new Color().setRGB(0.36, 0.34, 0.4),
  sun: new Color().setRGB(1.0, 0.7, 0.4),
};

const SKY_VERTEX = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = position;
    vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = p.xyww;
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 zenith;
  uniform vec3 horizon;
  uniform vec3 horizonSun;
  uniform vec3 groundColor;
  uniform vec3 sunDir;
  uniform vec3 sunColor;
  uniform float time;
  uniform float clouds;
  varying vec3 vDir;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * noise(p);
      p = p * 2.03 + 17.0;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 d = normalize(vDir);
    float h = d.y;
    vec2 flatDir = normalize(d.xz + vec2(1e-5));
    vec2 flatSun = normalize(sunDir.xz + vec2(1e-5));
    float towardSun = pow(max(dot(flatDir, flatSun), 0.0), 2.5);
    vec3 horizonColor = mix(horizon, horizonSun, towardSun * 0.85);
    vec3 col = mix(horizonColor, zenith, pow(clamp(h, 0.0, 1.0), 0.5));
    col = mix(col, groundColor, smoothstep(0.0, -0.12, h));
    float s = max(dot(d, sunDir), 0.0);
    col += sunColor * (pow(s, 1400.0) * 40.0 + pow(s, 32.0) * 0.5 + pow(s, 6.0) * 0.1);
    if (h > 0.0 && clouds > 0.0) {
      vec2 uv = d.xz / (h + 0.12) * 1.4 + vec2(time * 0.006, time * 0.002);
      float density = smoothstep(0.56, 0.84, fbm(uv));
      float shade = mix(0.72, 1.12, fbm(uv * 2.3 + 5.0));
      vec3 cloud = mix(vec3(1.0, 0.9, 0.84), sunColor * 1.5, pow(s, 5.0) * 0.7) * shade;
      col = mix(col, cloud, density * smoothstep(0.01, 0.18, h) * 0.8 * clouds);
    }
    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function skyMaterial(sunDirection: Vector3, clouds: number): ShaderMaterial {
  return new ShaderMaterial({
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    uniforms: {
      zenith: { value: SKY.zenith },
      horizon: { value: SKY.horizon },
      horizonSun: { value: SKY.horizonSun },
      groundColor: { value: SKY.ground },
      sunDir: { value: sunDirection },
      sunColor: { value: SKY.sun },
      time: { value: 0 },
      clouds: { value: clouds },
    },
    side: BackSide,
    depthWrite: false,
    fog: false,
  });
}

/**
 * Late-afternoon lighting: a hand-tuned gradient sky with sun glow and drifting clouds,
 * a warm low sun whose shadow frustum follows the local rider, a hemisphere fill,
 * sky-derived image-based lighting and horizon-matched haze.
 */
export class Environment {
  public readonly sun: DirectionalLight;
  public readonly sunDirection = new Vector3();
  private readonly sky: Mesh;
  private readonly skyMaterial: ShaderMaterial;
  private readonly hemi: HemisphereLight;
  private readonly shadowSpan = 46;

  public constructor(
    private readonly scene: Scene,
    renderer: WebGLRenderer,
    options: EnvironmentOptions,
  ) {
    const elevation = 17;
    const azimuth = 220;
    this.sunDirection.setFromSphericalCoords(
      1,
      MathUtils.degToRad(90 - elevation),
      MathUtils.degToRad(azimuth),
    );

    this.skyMaterial = skyMaterial(this.sunDirection, 1);
    this.sky = new Mesh(new SphereGeometry(4000, 48, 24), this.skyMaterial);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1;
    scene.add(this.sky);

    // Image-based lighting from the same sky (without clouds).
    const pmrem = new PMREMGenerator(renderer);
    const envScene = new Scene();
    const envSky = new Mesh(new SphereGeometry(100, 32, 16), skyMaterial(this.sunDirection, 0));
    envScene.add(envSky);
    scene.environment = pmrem.fromScene(envScene, 0.04).texture;
    scene.environmentIntensity = 0.7;
    pmrem.dispose();
    envSky.geometry.dispose();
    (envSky.material as ShaderMaterial).dispose();

    scene.fog = new FogExp2(SKY.horizon.clone(), 0.0011);

    this.hemi = new HemisphereLight(new Color('#bcd2ff'), new Color('#8a6448'), 0.6);
    scene.add(this.hemi);

    this.sun = new DirectionalLight(new Color('#ffd0a0'), 3.4);
    this.sun.position.copy(this.sunDirection).multiplyScalar(150);
    this.sun.castShadow = options.shadows;
    const shadow = this.sun.shadow;
    shadow.mapSize.set(options.shadowMapSize, options.shadowMapSize);
    shadow.camera.left = -this.shadowSpan;
    shadow.camera.right = this.shadowSpan;
    shadow.camera.top = this.shadowSpan;
    shadow.camera.bottom = -this.shadowSpan;
    shadow.camera.near = 10;
    shadow.camera.far = 400;
    shadow.bias = -0.0004;
    shadow.normalBias = 0.035;
    shadow.radius = 3;
    scene.add(this.sun);
    scene.add(this.sun.target);
  }

  public setShadows(enabled: boolean, mapSize: number): void {
    this.sun.castShadow = enabled;
    if (this.sun.shadow.mapSize.x !== mapSize) {
      this.sun.shadow.mapSize.set(mapSize, mapSize);
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
    }
  }

  /** Keeps the sky centred on the camera and the shadow frustum on `focus`. */
  public follow(focus: Vector3, camera: Vector3, time: number): void {
    this.sky.position.copy(camera);
    this.skyMaterial.uniforms.time!.value = time;
    const texel = (this.shadowSpan * 2) / this.sun.shadow.mapSize.x;
    const x = Math.round(focus.x / texel) * texel;
    const z = Math.round(focus.z / texel) * texel;
    this.sun.target.position.set(x, focus.y, z);
    this.sun.position.set(x, focus.y, z).addScaledVector(this.sunDirection, 150);
    this.sun.target.updateMatrixWorld();
  }

  public dispose(): void {
    this.scene.remove(this.sky, this.hemi, this.sun, this.sun.target);
    this.sky.geometry.dispose();
    this.skyMaterial.dispose();
    this.scene.environment?.dispose();
  }
}
